import { Box, Button, IconButton, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { getExternalLink } from "../links/ExternalLink";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import colors from "common/styles/colors";

type SuggestionSourceKey = "RC" | "Regia" | "ArcGisFlood" | "APVA";

export const SuggestionSource: Record<
  SuggestionSourceKey,
  {
    i18nLabel: string;
    url?: string;
  }
> = {
  RC: {
    i18nLabel: "propertyForm.suggestion.source.registryCentre",
    url: "https://www.registrucentras.lt/",
  },
  Regia: {
    i18nLabel: "propertyForm.suggestion.source.regia",
    url: "https://regia.lt/map/regia2",
  },
  ArcGisFlood: {
    i18nLabel: "propertyForm.suggestion.source.arcgisFlood",
    url: "https://experience.arcgis.com/experience/7f2d4ca0c74c4857a0620967e530fa4d",
  },
  APVA: {
    i18nLabel: "propertyForm.suggestion.source.apva",
    url: "https://renomap.apva.lt/map/",
  },
};

interface FormInputSuggestionProps<T> {
  gap?: number;
  suggestionLabel: string;
  suggestionAcceptLabel?: string;
  currentValue?: any;
  suggestedValue: T;
  onAgree: (value: NonNullable<T>) => void;
  source?: SuggestionSourceKey;
  address?: string;
  caption?: string;
}

export function FormInputSuggestion<T>({
  gap = 20,
  suggestionLabel,
  suggestionAcceptLabel: _suggestionAcceptLabel,
  suggestedValue,
  currentValue,
  onAgree,
  source,
  address,
  caption,
}: FormInputSuggestionProps<T>) {
  const { t } = useTranslation();
  const labelUseSuggestion =
    _suggestionAcceptLabel || t("propertyForm.suggestion.useThis");
  if (
    suggestedValue === undefined ||
    suggestedValue === null ||
    suggestedValue === currentValue
  ) {
    return null;
  }
  const sourceObj = source ? SuggestionSource[source] : undefined;

  return (
    <Box
      display="flex"
      alignItems="center"
      bgcolor={colors.bg}
      p={2}
      borderRadius={1.5}
      width="100%"
      maxWidth={500}
      style={{ marginTop: gap }}
    >
      <Tooltip
        title={
          t("propertyForm.suggestion.source.label") +
          " " +
          (sourceObj?.i18nLabel ? t(sourceObj.i18nLabel) : "-")
        }
      >
        <span /* I am here so tooltip can be shown even on disabled IconButon */
        >
          <IconButton
            href={getExternalLink(sourceObj?.url ?? "")}
            target="_blank"
            rel="noopener"
            sx={{ mr: 2 }}
            disabled={!sourceObj?.url}
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Box flexGrow={1} mr={2}>
        <Typography variant="subtitle1" mb={0}>
          {suggestionLabel}
        </Typography>
        <Typography variant="caption" color="text.secondary" mt={0}>
          {caption || t("propertyForm.suggestion.caption")}
          {address
            ? " " + t("propertyForm.suggestion.address", { address })
            : ""}
        </Typography>
      </Box>
      <Button
        size="small"
        variant="contained"
        style={{ padding: "0px 21px" /* pad sides */ }}
        onClick={() => onAgree(suggestedValue)}
      >
        {labelUseSuggestion}
      </Button>
    </Box>
  );
}
